import * as dagre from '@dagrejs/dagre';
import { Node, Edge } from 'reactflow';

export interface DagreLayoutOptions {
  direction?: 'TB' | 'LR';
  rankSep?: number;
  nodeSep?: number;
  edgeSep?: number;
}

export const DAGRE_LAYOUT_PRESETS = {
  comfortable: { label: 'Comfortable',  rankSep: 200, nodeSep: 160, edgeSep: 80  },
  spacious:    { label: 'Spacious',     rankSep: 280, nodeSep: 220, edgeSep: 100 },
  compact:     { label: 'Compact',      rankSep: 120, nodeSep: 80,  edgeSep: 40  },
  ultrawide:   { label: 'Ultra-wide',   rankSep: 180, nodeSep: 320, edgeSep: 80  },
  deep:        { label: 'Deep',         rankSep: 360, nodeSep: 140, edgeSep: 60  },
} as const;

export type DagreLayoutPreset = keyof typeof DAGRE_LAYOUT_PRESETS;

// ── Types ─────────────────────────────────────────────────────────────────────

interface Vec2 { x: number; y: number; }
interface PhysicsBody {
  id:       string;
  pos:      Vec2;
  vel:      Vec2;
  width:    number;
  height:   number;
  pinned:   boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const NODE_DIMENSIONS: Record<string, { width: number; height: number }> = {
  // Phase height uses the FULLY EXPANDED size so dagre reserves enough vertical
  // space that expanding a card never overlaps neighbours.
  // Expanded card ≈ header(80) + mission(40) + personas(80) + tools(60)
  //               + objectives(60) + success criteria(50) + notes(40) + padding(70) = 480px
  phase:    { width: 320, height: 480 },
  tool:     { width: 220, height: 100 },
  persona:  { width: 240, height: 110 },
  artifact: { width: 200, height: 90  },
  default:  { width: 220, height: 100 },
};

// Flow edges drive DAG rank assignment
const FLOW_EDGE_TYPES = new Set(['flow', 'requires', 'default', undefined, null, '']);

// Assignment edges connect entities to phases (satellite relationships)
const ASSIGNMENT_EDGE_TYPES = new Set([
  'tool_assignment',
  'persona_assignment',
  'input_artifact',
  'output_artifact',
]);

const TRANSITION = 'transform 600ms cubic-bezier(0.4, 0, 0.2, 1)';

// ── Overlap resolution ────────────────────────────────────────────────────────

interface Rect {
  id: string;
  x:  number;  // top-left
  y:  number;
  w:  number;
  h:  number;
}

/**
 * Iterative separation pass — pushes overlapping rectangles apart until
 * no two rects overlap (or max iterations reached).
 * Rects whose ids are in `pinnedIds` are treated as immovable obstacles.
 */
function resolveOverlaps(
  rects: Rect[],
  pinnedIds: Set<string>,
  padding: number = 24,
  maxIterations: number = 80
): Map<string, { x: number; y: number }> {
  const boxes = rects.map(r => ({ ...r }));

  for (let iter = 0; iter < maxIterations; iter++) {
    let anyOverlap = false;

    for (let a = 0; a < boxes.length; a++) {
      for (let b = a + 1; b < boxes.length; b++) {
        const ba = boxes[a];
        const bb = boxes[b];

        // Penetration depths on each axis
        const penRight  = (ba.x + ba.w + padding) - bb.x;
        const penLeft   = (bb.x + bb.w + padding) - ba.x;
        const penBottom = (ba.y + ba.h + padding) - bb.y;
        const penTop    = (bb.y + bb.h + padding) - ba.y;

        // No overlap if separated on either axis
        if (penRight <= 0 || penLeft <= 0 || penBottom <= 0 || penTop <= 0) continue;

        anyOverlap = true;

        const penX = Math.min(penRight, penLeft);
        const penY = Math.min(penBottom, penTop);

        const aPinned = pinnedIds.has(ba.id);
        const bPinned = pinnedIds.has(bb.id);

        if (penX < penY) {
          // Separate on X axis — minimum penetration
          const push = penX / 2 + 1;
          if (ba.x < bb.x) {
            if (!aPinned) ba.x -= push;
            if (!bPinned) bb.x += push;
          } else {
            if (!aPinned) ba.x += push;
            if (!bPinned) bb.x -= push;
          }
        } else {
          // Separate on Y axis — minimum penetration
          const push = penY / 2 + 1;
          if (ba.y < bb.y) {
            if (!aPinned) ba.y -= push;
            if (!bPinned) bb.y += push;
          } else {
            if (!aPinned) ba.y += push;
            if (!bPinned) bb.y -= push;
          }
        }
      }
    }

    if (!anyOverlap) {
      console.log(`🔲 OVERLAP-RESOLVE: Converged after ${iter + 1} iterations`);
      break;
    }
  }

  const result = new Map<string, { x: number; y: number }>();
  boxes.forEach(b => result.set(b.id, { x: b.x, y: b.y }));
  return result;
}

// ── Force simulation for entity nodes ────────────────────────────────────────

/**
 * Run a mini force-directed simulation on entity nodes only.
 * Phase nodes are static obstacles. Entity nodes are attracted to their
 * connected phase center and repel each other and phase boxes.
 */
function forceSimulateEntities(
  entityNodes: Node[],
  phasePositionMap: Map<string, { x: number; y: number }>,
  assignmentEdges: Edge[],
  nodeSep: number,
  iterations: number = 300   // more iterations → full convergence every run
): Map<string, Vec2> {
  const DAMPING          = 0.65;  // tighter damping → settles to same place every time
  const ENTITY_REPULSE   = Math.max(nodeSep * 2.5, 380);
  const PHASE_REPULSE    = Math.max(nodeSep * 1.8, 260);
  const ATTRACT_STRENGTH = 0.18;  // stronger pull → snaps to target deterministically

  // Build physics bodies seeded at their ideal target positions
  const bodies = new Map<string, PhysicsBody>();

  entityNodes.forEach(node => {
    const dims = NODE_DIMENSIONS[node.type ?? 'default'] ?? NODE_DIMENSIONS.default;

    const connectedEdge = assignmentEdges.find(
      e => e.source === node.id || e.target === node.id
    );
    const connectedPhaseId = connectedEdge
      ? (connectedEdge.source === node.id ? connectedEdge.target : connectedEdge.source)
      : null;
    const phasePos = connectedPhaseId ? phasePositionMap.get(connectedPhaseId) : null;

    const nodeType = node.type ?? 'default';
    const gap = Math.max(nodeSep * 3.0, 320);
    let seedX: number;
    let seedY: number;

    if (phasePos) {
      const phaseCx = phasePos.x + NODE_DIMENSIONS.phase.width  / 2;
      const phaseCy = phasePos.y + NODE_DIMENSIONS.phase.height / 2;

      if (nodeType === 'tool') {
        seedX = phaseCx - NODE_DIMENSIONS.phase.width / 2 - dims.width / 2 - gap;
        seedY = phaseCy;
      } else if (nodeType === 'persona') {
        seedX = phaseCx + NODE_DIMENSIONS.phase.width / 2 + dims.width / 2 + gap;
        seedY = phaseCy;
      } else if (nodeType === 'artifact') {
        const isInput = connectedEdge && connectedEdge.source === node.id;
        seedX = phaseCx;
        seedY = isInput
          ? phaseCy - NODE_DIMENSIONS.phase.height / 2 - dims.height / 2 - gap
          : phaseCy + NODE_DIMENSIONS.phase.height / 2 + dims.height / 2 + gap;
      } else {
        seedX = phaseCx + NODE_DIMENSIONS.phase.width / 2 + dims.width / 2 + gap;
        seedY = phaseCy;
      }
    } else {
      seedX = (Math.random() - 0.5) * 800;
      seedY = (Math.random() - 0.5) * 600;
    }

    // Deterministic seed — no random jitter so layout is identical every run
    bodies.set(node.id, {
      id:     node.id,
      pos:    { x: seedX, y: seedY },
      vel:    { x: 0, y: 0 },
      width:  dims.width,
      height: dims.height,
      pinned: false,
    });
  });

  // Build static phase obstacle list (center + half-dims)
  const phaseObstacles: Array<{ cx: number; cy: number; hw: number; hh: number }> = [];
  phasePositionMap.forEach(pos => {
    phaseObstacles.push({
      cx: pos.x + NODE_DIMENSIONS.phase.width  / 2,
      cy: pos.y + NODE_DIMENSIONS.phase.height / 2,
      hw: NODE_DIMENSIONS.phase.width  / 2,
      hh: NODE_DIMENSIONS.phase.height / 2,
    });
  });

  // Build attraction targets using same gap logic as seed positions
  const attractTargets = new Map<string, Vec2>();
  entityNodes.forEach(node => {
    const connectedEdge = assignmentEdges.find(
      e => e.source === node.id || e.target === node.id
    );
    if (!connectedEdge) return;
    const phaseId = connectedEdge.source === node.id
      ? connectedEdge.target
      : connectedEdge.source;
    const phasePos = phasePositionMap.get(phaseId);
    if (!phasePos) return;

    const nodeType = node.type ?? 'default';
    const dims = NODE_DIMENSIONS[nodeType] ?? NODE_DIMENSIONS.default;
    const gap = Math.max(nodeSep * 3.0, 320);
    const phaseCx = phasePos.x + NODE_DIMENSIONS.phase.width  / 2;
    const phaseCy = phasePos.y + NODE_DIMENSIONS.phase.height / 2;

    let tx: number;
    let ty: number;

    if (nodeType === 'tool') {
      tx = phaseCx - NODE_DIMENSIONS.phase.width / 2 - dims.width / 2 - gap;
      ty = phaseCy;
    } else if (nodeType === 'persona') {
      tx = phaseCx + NODE_DIMENSIONS.phase.width / 2 + dims.width / 2 + gap;
      ty = phaseCy;
    } else if (nodeType === 'artifact') {
      // Input artifact (entity is source → feeds into phase): above phase
      // Output artifact (entity is target ← phase feeds into it): below phase
      const isInput = connectedEdge.source === node.id;
      tx = phaseCx;
      ty = isInput
        ? phaseCy - NODE_DIMENSIONS.phase.height / 2 - dims.height / 2 - gap
        : phaseCy + NODE_DIMENSIONS.phase.height / 2 + dims.height / 2 + gap;
    } else {
      tx = phaseCx + NODE_DIMENSIONS.phase.width / 2 + dims.width / 2 + gap;
      ty = phaseCy;
    }

    attractTargets.set(node.id, { x: tx, y: ty });
  });

  // ── Simulation loop ─────────────────────────────────────────────────────────
  const bodyList = Array.from(bodies.values());

  for (let iter = 0; iter < iterations; iter++) {
    // 1. Strong attraction toward ideal target position
    bodyList.forEach(body => {
      const target = attractTargets.get(body.id);
      if (!target) return;
      const dx = target.x - body.pos.x;
      const dy = target.y - body.pos.y;
      body.vel.x += dx * ATTRACT_STRENGTH;
      body.vel.y += dy * ATTRACT_STRENGTH;
    });

    // 2. Entity ↔ entity repulsion
    for (let a = 0; a < bodyList.length; a++) {
      for (let b = a + 1; b < bodyList.length; b++) {
        const ba = bodyList[a];
        const bb = bodyList[b];
        const dx = bb.pos.x - ba.pos.x;
        const dy = bb.pos.y - ba.pos.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const minDist = ENTITY_REPULSE;
        if (dist < minDist) {
          const force = (minDist - dist) / dist * 0.5;
          ba.vel.x -= dx * force;
          ba.vel.y -= dy * force;
          bb.vel.x += dx * force;
          bb.vel.y += dy * force;
        }
      }
    }

    // 3. Entity ↔ phase repulsion (keep entities clear of phase boxes)
    bodyList.forEach(body => {
      phaseObstacles.forEach(obs => {
        const dx = body.pos.x - obs.cx;
        const dy = body.pos.y - obs.cy;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        // Effective clearance: sum of half-widths + padding
        const clearance = obs.hw + body.width / 2 + PHASE_REPULSE * 0.3;
        if (dist < clearance) {
          const force = (clearance - dist) / dist * 0.8;
          body.vel.x += dx * force;
          body.vel.y += dy * force;
        }
      });
    });

    // 4. Integrate + damp
    bodyList.forEach(body => {
      body.pos.x += body.vel.x;
      body.pos.y += body.vel.y;
      body.vel.x *= DAMPING;
      body.vel.y *= DAMPING;
    });
  }

  // Return top-left positions (ReactFlow convention)
  const result = new Map<string, Vec2>();
  bodyList.forEach(body => {
    result.set(body.id, {
      x: body.pos.x - body.width  / 2,
      y: body.pos.y - body.height / 2,
    });
  });
  return result;
}

// ── Main layout function ──────────────────────────────────────────────────────

/**
 * Apply a full DAG layout to the workflow graph.
 *
 * 1. Pin the root phase node at top-center.
 * 2. Dagre lays out all phase nodes flowing downward from root.
 * 3. Center the graph horizontally.
 * 4. Force simulation places entity nodes (tool/persona/artifact) —
 *    attracted to their connected phase, repelling each other and phase boxes.
 *
 * Returns Node[] with updated positions and CSS transitions.
 */
export function applyDagreLayout(
  nodes: Node[],
  edges: Edge[],
  options: DagreLayoutOptions = {}
): Node[] {
  const {
    direction = 'TB',
    rankSep   = DAGRE_LAYOUT_PRESETS.comfortable.rankSep,
    nodeSep   = DAGRE_LAYOUT_PRESETS.comfortable.nodeSep,
    edgeSep   = DAGRE_LAYOUT_PRESETS.comfortable.edgeSep,
  } = options;

  if (nodes.length === 0) return nodes;

  // ── 1. Separate nodes and edges ────────────────────────────────────────────
  const phaseNodes      = nodes.filter(n => n.type === 'phase');
  const entityNodes     = nodes.filter(n => n.type !== 'phase');
  const phaseIdSet      = new Set(phaseNodes.map(n => n.id));
  const flowEdges       = edges.filter(e => FLOW_EDGE_TYPES.has(e.data?.type));
  const assignmentEdges = edges.filter(e => ASSIGNMENT_EDGE_TYPES.has(e.data?.type));

  if (phaseNodes.length === 0) {
    // No phases — just run force sim on everything
    const tempMap = new Map<string, { x: number; y: number }>();
    const positions = forceSimulateEntities(nodes, tempMap, assignmentEdges, nodeSep);
    return nodes.map(n => ({
      ...n,
      position: positions.get(n.id) ?? n.position,
      style: { ...n.style, transition: TRANSITION },
    }));
  }

  // ── 2. Find root phase (no incoming flow edges among phases) ───────────────
  const incomingPhaseEdges = new Set<string>();
  flowEdges.forEach(e => {
    if (phaseIdSet.has(e.source) && phaseIdSet.has(e.target)) {
      incomingPhaseEdges.add(e.target);
    }
  });
  const rootPhaseId = phaseNodes.find(n => !incomingPhaseEdges.has(n.id))?.id
    ?? phaseNodes[0].id;

  // ── 3. Build dagre graph ───────────────────────────────────────────────────
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: direction, ranksep: rankSep, nodesep: nodeSep, edgesep: edgeSep });

  phaseNodes.forEach(node => {
    const dims = NODE_DIMENSIONS[node.type] ?? NODE_DIMENSIONS.default;
    g.setNode(node.id, { width: dims.width, height: dims.height });
  });

  flowEdges.forEach(edge => {
    if (phaseIdSet.has(edge.source) && phaseIdSet.has(edge.target)) {
      g.setEdge(edge.source, edge.target);
    }
  });

  dagre.layout(g);

  // ── 4. Anchor root phase at top-center (0, 80) then shift everything ───────
  const rootDagreNode = g.node(rootPhaseId);
  const rootDims      = NODE_DIMENSIONS.phase;

  // Root's top-left in dagre coords
  const rootDagreLeft = rootDagreNode.x - rootDims.width  / 2;
  const rootDagreTop  = rootDagreNode.y - rootDims.height / 2;

  // We want root's top-left to land at (-rootDims.width/2, 80)
  // so the root center sits at (0, 80 + rootDims.height/2) — top-center of canvas
  const shiftX = -(rootDims.width / 2) - rootDagreLeft;
  const shiftY = 80 - rootDagreTop;

  // ── 5. Extract raw phase positions from dagre ──────────────────────────────
  const rawPhasePositions = new Map<string, { x: number; y: number }>();
  phaseNodes.forEach(node => {
    const dn   = g.node(node.id);
    const dims = NODE_DIMENSIONS[node.type] ?? NODE_DIMENSIONS.default;
    rawPhasePositions.set(node.id, {
      x: dn.x - dims.width  / 2 + shiftX,
      y: dn.y - dims.height / 2 + shiftY,
    });
  });

  // ── 5a. Resolve phase↔phase overlaps ──────────────────────────────────────
  // Dagre can still produce overlaps with tight nodeSep or parallel branches.
  // Pin the root so it stays anchored at top-center.
  const phaseRects: Rect[] = phaseNodes.map(node => {
    const pos  = rawPhasePositions.get(node.id)!;
    const dims = NODE_DIMENSIONS.phase;
    return { id: node.id, x: pos.x, y: pos.y, w: dims.width, h: dims.height };
  });

  const resolvedPhasePositions = resolveOverlaps(
    phaseRects,
    new Set([rootPhaseId]),
    48   // generous padding — cards expand to ~480px tall
  );

  const phasePositionMap = new Map<string, { x: number; y: number }>();
  phaseNodes.forEach(node => {
    phasePositionMap.set(
      node.id,
      resolvedPhasePositions.get(node.id) ?? rawPhasePositions.get(node.id)!
    );
  });

  const laidOutPhaseNodes: Node[] = phaseNodes.map(node => ({
    ...node,
    position: phasePositionMap.get(node.id)!,
    style: { ...node.style, transition: TRANSITION },
  }));

  // ── 6. Force simulate entity nodes ────────────────────────────────────────
  const entityPositions = forceSimulateEntities(
    entityNodes,
    phasePositionMap,
    assignmentEdges,
    nodeSep
  );

  // ── 6a. Resolve entity↔entity and entity↔phase overlaps ───────────────────
  // Build a combined rect list: phases are pinned, entities are free.
  const allRects: Rect[] = [
    ...phaseNodes.map(node => {
      const pos  = phasePositionMap.get(node.id)!;
      const dims = NODE_DIMENSIONS.phase;
      return { id: node.id, x: pos.x, y: pos.y, w: dims.width, h: dims.height };
    }),
    ...entityNodes.map(node => {
      const pos  = entityPositions.get(node.id) ?? node.position;
      const dims = NODE_DIMENSIONS[node.type ?? 'default'] ?? NODE_DIMENSIONS.default;
      return { id: node.id, x: pos.x, y: pos.y, w: dims.width, h: dims.height };
    }),
  ];

  const pinnedPhaseIds = new Set(phaseNodes.map(n => n.id));
  const resolvedAllPositions = resolveOverlaps(allRects, pinnedPhaseIds, 40);

  const laidOutEntityNodes: Node[] = entityNodes.map(node => ({
    ...node,
    position: resolvedAllPositions.get(node.id) ?? entityPositions.get(node.id) ?? node.position,
    style: { ...node.style, transition: TRANSITION },
  }));

  const result = [...laidOutPhaseNodes, ...laidOutEntityNodes];

  console.log('🗺️ DAGRE+FORCE+OVERLAP LAYOUT: Complete', {
    phaseNodes:   laidOutPhaseNodes.length,
    entityNodes:  laidOutEntityNodes.length,
    rootPhase:    rootPhaseId,
    direction,
    rankSep,
    nodeSep,
    shiftX,
    shiftY,
  });

  return result;
}
